import React from "react";
import { Ban, ReceiptText } from "lucide-react";
import { getPaymentMethodLabel } from "../../constant/paymentMethod";

const statusStyles = {
    PAID: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    PENDING: "bg-amber-50 text-amber-700 ring-amber-200",
    CANCELLED: "bg-slate-100 text-slate-600 ring-slate-200",
    VOIDED: "bg-rose-50 text-rose-700 ring-rose-200",
};

const formatStatus = (status) => {
    const normalized = String(status || "").toUpperCase();
    if (normalized === "PAID") return "Paid";
    if (normalized === "VOIDED") return "Voided";
    if (normalized === "CANCELLED") return "Cancelled";
    if (normalized === "PENDING") return "Pending";
    return status || "-";
};

export default function TransactionCard({ tx, onClick, onVoid }) {
    // Format date and time (use created_at from backend)
    const dateObj = new Date(tx.created_at);
    const dateStr = isNaN(dateObj) ? '-' : dateObj.toLocaleDateString();
    const timeStr = isNaN(dateObj) ? '-' : dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const products = Array.isArray(tx.products) ? tx.products : [];
    const status = String(tx.status || "").toUpperCase();
    const canVoid = status === "PAID" && typeof onVoid === "function";
    const isManualOnline = tx.payment_collection_mode === "MANUAL_ONLINE";

    return (
        <div
            className="flex min-h-[156px] w-full flex-col rounded-2xl border border-slate-200 bg-white p-3 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md sm:max-w-xs sm:flex-1"
            onClick={e => {
                if (e.target.tagName === 'BUTTON') return;
                if (onClick) onClick();
            }}
            tabIndex={0}
            role="button"
            aria-label={`View details for order ${tx.order_number}`}
        >
            {/* Order Number, Status, and Branch Row */}
            <div className="mb-1 flex items-start justify-between gap-2">
                <span className="min-w-0 truncate text-xs font-bold text-slate-900" title={`Order #${tx.order_number}`}>#{tx.order_number}</span>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ring-1 ${statusStyles[status] || "bg-slate-100 text-slate-600 ring-slate-200"}`}>
                    {formatStatus(tx.status)}
                </span>
            </div>
            {/* Branch Name */}
            {tx.branch_name && (
                <div className="mb-1 truncate text-[10px] font-medium italic text-slate-500">{tx.branch_name}</div>
            )}
            {/* Date, Time, Payment */}
            <div className="mb-2 flex justify-between gap-2 text-[10px] text-slate-500">
                <span>{dateStr}</span>
                <span>{timeStr}</span>
                <span className="italic">{getPaymentMethodLabel(tx.payment_method)}</span>
            </div>
            {isManualOnline && tx.payment_reference_last4 && (
                <div className="mb-2 w-fit rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 ring-1 ring-emerald-100">
                    Manual ref •••• {tx.payment_reference_last4}
                </div>
            )}
            {/* Products (inline, compact) */}
            {products.length > 0 && (
                <div className="mb-2 line-clamp-2 min-h-[32px] text-[11px] leading-4 text-slate-700">
                    {products.slice(0, 3).map((item, idx) => (
                        <span key={idx} className="mr-2">
                            {item.name || "Item"} <span className="text-slate-500">x{item.qty || 1}</span>{idx < Math.min(products.length, 3) - 1 ? ',' : ''}
                        </span>
                    ))}
                    {products.length > 3 && <span className="text-slate-400">+{products.length - 3} more</span>}
                </div>
            )}
            {/* Total Amount Paid */}
            <div className="mt-auto flex items-center justify-between gap-2">
                <span className={`text-xs font-bold ${status === "VOIDED" ? "text-slate-400 line-through" : "text-slate-900"}`}>
                    ₱{Number(tx.total).toLocaleString()}
                </span>
                {tx.seat_number && (
                    <span className="hidden text-[10px] text-slate-500 sm:inline">Seat: {tx.seat_number}</span>
                )}
                <div className="ml-auto flex items-center gap-1.5">
                    {canVoid && (
                        <button
                            type="button"
                            onClick={(event) => {
                                event.stopPropagation();
                                onVoid?.(tx);
                            }}
                            className="inline-flex h-8 items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-2 text-[11px] font-semibold text-rose-700 transition hover:bg-rose-100"
                            title="Void order"
                        >
                            <Ban size={13} />
                            Void
                        </button>
                    )}
                    <button
                        type="button"
                        tabIndex={0}
                        onClick={(event) => {
                            event.stopPropagation();
                            onClick?.();
                        }}
                        className="inline-flex h-8 items-center gap-1 rounded-lg bg-slate-100 px-2 text-[11px] font-semibold text-slate-700 transition hover:bg-slate-200"
                        title="View transaction details"
                    >
                        <ReceiptText size={13} />
                        Details
                    </button>
                </div>
            </div>
        </div>
    );
}
